import createCachedSelector from 're-reselect';

import type { Campaign, CampaignReference, CampaignWithReference, EntityStatus, YammerState } from '../../state/types';
import { getStatusForId } from '../../state/types';
import { getFulfilledCampaignCoverImages } from '../campaignCoverImage/selectors';
import { createFindById } from '../findById';

import type { TrendingCampaignsLoadOptions } from './loadActions';

export const findById = createFindById('trendingCampaigns');

type GetTrendingCampaignsStatus = (state: YammerState, options: TrendingCampaignsLoadOptions) => EntityStatus;
export const getTrendingCampaignsStatus: GetTrendingCampaignsStatus = (state, options) =>
  getStatusForId(state.trendingCampaigns, getTrendingCampaignsKey(options));

const emptyCampaignIds: Campaign['id'][] = [];
export const getTrendingCampaignsIdsFromState = (state: YammerState, options: TrendingCampaignsLoadOptions) =>
  findById(state, getTrendingCampaignsKey(options))?.campaignIds ?? emptyCampaignIds;

type GetFulfilledCampaigns = (state: YammerState) => Record<string, Campaign>;
export const getFulfilledCampaigns: GetFulfilledCampaigns = (state) => state.campaigns.fulfilled.byId;

type GetFulfilledCampaignReferences = (state: YammerState) => Record<string, CampaignReference>;
export const getFulfilledCampaignReferences: GetFulfilledCampaignReferences = (state) =>
  state.campaignReferences.fulfilled.byId;

type GetMostTrendingCampaignId = (state: YammerState, options: TrendingCampaignsLoadOptions) => string | undefined;
export const getMostTrendingCampaignId: GetMostTrendingCampaignId = (
  state: YammerState,
  options: TrendingCampaignsLoadOptions
) => {
  const campaignIds = findById(state, getTrendingCampaignsKey(options))?.campaignIds;

  return campaignIds?.[0];
};

type GetTrendingCampaignsKey = (options: TrendingCampaignsLoadOptions) => string;
export const getTrendingCampaignsKey: GetTrendingCampaignsKey = ({ count, followersCount, filterBy }) =>
  `${count}-${followersCount}-${filterBy}`;

type GetTrendingCampaignsWithSelectedCampaign = (
  state: YammerState,
  selectedCampaignId: Campaign['id'],
  options: TrendingCampaignsLoadOptions
) => CampaignWithReference[];
export const getTrendingCampaignsWithSelectedCampaign: GetTrendingCampaignsWithSelectedCampaign = createCachedSelector(
  getFulfilledCampaigns,
  getFulfilledCampaignReferences,
  getFulfilledCampaignCoverImages,
  (state: YammerState, _, options) => getTrendingCampaignsIdsFromState(state, options),
  (_state: YammerState, selectedCampaignId: Campaign['id']) => selectedCampaignId,
  (
    fulfilledCampaigns,
    fulfilledCampaignReferences,
    fulfilledCampaignCoverImages,
    trendingCampaignIds,
    selectedCampaignId
  ) => {
    const campaigns = trendingCampaignIds
      .filter((id) => {
        const campaign = fulfilledCampaigns[id];
        const isSelected = selectedCampaignId === id;

        if (!campaign) {
          throw new Error('Missing campaign data in state for trending campaign edge.');
        }

        return !isSelected;
      })
      .map((id) => ({
        ...fulfilledCampaigns[id],
        ...fulfilledCampaignReferences[id],
        ...fulfilledCampaignCoverImages[id],
      }));

    return fulfilledCampaigns[selectedCampaignId]
      ? [
          {
            ...fulfilledCampaigns[selectedCampaignId],
            ...fulfilledCampaignReferences[selectedCampaignId],
            ...fulfilledCampaignCoverImages[selectedCampaignId],
          },
          ...campaigns,
        ]
      : campaigns;
  }
)((_state, selectedCampaignId) => `trending-${selectedCampaignId}}`);

type GetTrendingCampaignsWithReference = (
  state: YammerState,
  options: TrendingCampaignsLoadOptions
) => CampaignWithReference[];
export const getTrendingCampaignsWithReference: GetTrendingCampaignsWithReference = createCachedSelector(
  getTrendingCampaignsIdsFromState,
  getFulfilledCampaigns,
  getFulfilledCampaignReferences,
  getFulfilledCampaignCoverImages,
  (trendingCampaignIds, fulfilledCampaigns, fulfilledCampaignReferences, fulfilledCampaignCoverImages) =>
    trendingCampaignIds.map((id) => {
      const campaign = fulfilledCampaigns[id];
      if (!campaign) {
        throw new Error('Missing campaign data in state for trending campaign edge.');
      }

      return {
        ...fulfilledCampaigns[id],
        ...fulfilledCampaignReferences[id],
        ...fulfilledCampaignCoverImages[id],
      };
    })
)((_state) => `trendingCampaigns`);

type HasNextPageOfTrendingCampaignsListAvailable = (
  state: YammerState,
  options: TrendingCampaignsLoadOptions
) => boolean;
export const hasNextPageOfTrendingCampaignsListAvailable: HasNextPageOfTrendingCampaignsListAvailable = (
  state,
  options
) => findById(state, getTrendingCampaignsKey(options))?.hasNextPage ?? false;
